import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import i18next from "i18next";
import {
  normalizeVehicleLanguage,
  vehicleTypeLabel,
  vehicleTypeMeta,
  vehicleTypeOptions,
} from "./vehicleCatalog.js";
import VehicleTypeIcon from "./VehicleTypeIcon.jsx";

const COPY = {
  ru: { search: "Поиск машины", all: "Любой кузов", choose: "Выберите тип машины", done: "Готово", close: "Закрыть", noResults: "Ничего не найдено" },
  uz: { search: "Машина қидириш", all: "Барча кузовлар", choose: "Машина турини танланг", done: "Тайёр", close: "Ёпиш", noResults: "Ҳеч нарса топилмади" },
  tr: { search: "Araç ara", all: "Tüm kasa tipleri", choose: "Araç tipini seçin", done: "Bitti", close: "Kapat", noResults: "Sonuç bulunamadı" },
};

export default function VehicleTypePicker({
  value = "",
  onChange,
  domain = "vehicle",
  multiple = false,
  allowAll = false,
  label = "",
  className = "",
  disabled = false,
}) {
  const lang = normalizeVehicleLanguage(i18next.language);
  const t = COPY[lang] || COPY.ru;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const values = useMemo(
    () => (multiple ? (Array.isArray(value) ? value : []) : [String(value || "")]).map((x) => String(x || "").toUpperCase()).filter(Boolean),
    [multiple, value]
  );

  const options = useMemo(() => {
    const q = query.trim().toLocaleLowerCase(lang === "tr" ? "tr-TR" : lang === "uz" ? "uz-UZ" : "ru-RU");
    return vehicleTypeOptions(domain).filter(({ code }) => {
      if (!q) return true;
      return `${vehicleTypeLabel(code, lang)} ${vehicleTypeMeta(code, lang)} ${code}`.toLocaleLowerCase().includes(q);
    });
  }, [domain, lang, query]);

  const selectedLabel = multiple
    ? values.map((code) => vehicleTypeLabel(code, lang)).join(" · ")
    : values[0]
      ? vehicleTypeLabel(values[0], lang)
      : (allowAll ? t.all : t.choose);

  function select(code) {
    if (multiple) {
      const next = values.includes(code) ? values.filter((x) => x !== code) : [...values, code];
      onChange?.(next);
      return;
    }
    onChange?.(code);
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={`flex min-h-[48px] w-full items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-950 px-3.5 py-2.5 text-left text-[15px] font-bold text-white outline-none transition focus:border-blue-500 disabled:opacity-50 ${className}`.trim()}
        aria-haspopup="dialog"
        data-reisor-vehicle-picker-trigger="v4"
      >
        <span className="min-w-0">
          {label ? <span className="mb-0.5 block text-[13px] font-semibold text-slate-400">{label}</span> : null}
          <span className="block truncate">{selectedLabel}</span>
          {!multiple && values[0] ? <span className="mt-0.5 block text-[13px] font-medium text-slate-400">{vehicleTypeMeta(values[0], lang)}</span> : null}
        </span>
        <ChevronDown className="h-5 w-5 shrink-0 text-slate-400" />
      </button>

      {open ? (
        <div className="fixed inset-0 z-[10150] flex items-end bg-black/65 sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" data-reisor-vehicle-picker-overlay="v4">
          <div className="max-h-[88dvh] w-full overflow-hidden rounded-t-3xl bg-slate-900 shadow-2xl sm:max-w-xl sm:rounded-3xl sm:border sm:border-slate-700" data-reisor-vehicle-picker-panel="v4">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-4" data-reisor-vehicle-picker-header="v4">
              <div className="text-lg font-black text-white">{t.choose}</div>
              <button type="button" onClick={() => setOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-white" aria-label={t.close}><X className="h-5 w-5" /></button>
            </div>

            <div className="p-4 pb-2">
              <label className="flex min-h-[48px] items-center gap-3 rounded-2xl bg-slate-950 px-4" data-reisor-vehicle-picker-search="v4">
                <Search className="h-5 w-5 shrink-0 text-slate-500" />
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t.search} className="min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-white outline-none placeholder:text-slate-500" />
              </label>
            </div>

            <div className="max-h-[62dvh] overflow-y-auto px-4 pb-4">
              {allowAll && !multiple ? (
                <button type="button" onClick={() => select("")} className="flex w-full items-center gap-3 border-b border-slate-800 py-4 text-left" data-reisor-vehicle-picker-option="v4">
                  <span className="flex h-12 w-14 items-center justify-center rounded-xl bg-slate-800"><VehicleTypeIcon code="OTHER" className="h-9 w-14 text-slate-300" /></span>
                  <span className="min-w-0 flex-1 text-[16px] font-black text-white">{t.all}</span>
                  {!values.length ? <Check className="h-5 w-5 text-blue-400" /> : null}
                </button>
              ) : null}

              {options.map(({ code }) => {
                const active = values.includes(code);
                return (
                  <button key={code} type="button" onClick={() => select(code)} className="flex w-full items-center gap-3 border-b border-slate-800 py-3.5 text-left last:border-b-0" data-reisor-vehicle-picker-option="v4">
                    <span className={`flex h-12 w-14 items-center justify-center rounded-xl ${active ? "bg-blue-600/20" : "bg-slate-800"}`}><VehicleTypeIcon code={code} className={`h-9 w-14 ${active ? "text-blue-300" : "text-slate-300"}`} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[16px] font-black text-white">{vehicleTypeLabel(code, lang)}</span>
                      <span className="mt-0.5 block text-[14px] font-medium text-slate-400">{vehicleTypeMeta(code, lang) || "—"}</span>
                    </span>
                    {active ? <Check className="h-5 w-5 shrink-0 text-blue-400" /> : null}
                  </button>
                );
              })}
              {!options.length ? <div className="py-8 text-center text-[15px] font-semibold text-slate-400">{t.noResults}</div> : null}
            </div>

            {multiple ? (
              <div className="border-t border-slate-800 p-4">
                <button type="button" onClick={() => setOpen(false)} className="min-h-[50px] w-full rounded-2xl bg-blue-600 px-4 text-[16px] font-black text-white">{t.done}</button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
